<template>
  <clinician-app-layout>
    <template #header>
      <div
        class="pb-5 max-w-6xl mx-auto border-gray-200 sm:flex sm:items-center sm:justify-between"
      >
        <h2 class="text-3xl text-vice-teal-900 font-semibold leading-tight">
          {{ !isDraft() ? 'Update session' : 'Create session' }}
        </h2>
        <div class="mt-3 flex sm:mt-0 sm:ml-4">
          <button
            data-cy="delete-draft"
            type="button"
            @click="open = true"
            class="
              inline-flex
              items-center
              justify-center
              px-4
              py-2
              border border-gray-300
              font-medium
              rounded-md
              text-gray-700
              bg-white
              hover:bg-gray-50
              focus:outline-none
              focus:ring-2 focus:ring-offset-2 focus:ring-gray-500
              sm:text-sm
            "
          >
            <DeleteSessionIcon class="h-5 w-5 mr-2 text-red-700" />
            Delete
            <span v-if="isDraft()">&nbsp;draft</span>
            <span v-else>&nbsp;session</span>
          </button>
          <button
            data-cy="save-as-draft"
            v-if="isDraft()"
            type="button"
            @click="this.disableButton ? 'javascript:info(0)' : saveSession(SESSION_STATUS.DRAFT)"
            :class="this.disableButton ? 'disabled:cursor-wait disabled:opacity-60' : ''"
            class="
              step-save-draft
              inline-flex
              items-center
              ml-3
              px-4
              py-2
              border border-gray-300
              rounded-md
              shadow-sm
              text-sm
              font-medium
              text-gray-700
              bg-white
              hover:bg-gray-50
              focus:outline-none
              focus:ring-2 focus:ring-offset-2 focus:ring-gray-500
            "
            :disabled="this.disableButton"
          >
            <SaveAsDraftIcon class="h-5 w-5 mr-2" />
            Save as draft
          </button>
          <button
            data-cy="publish-session"
            v-if="isDraft()"
            type="button"
            @click="this.disableButton ? 'javascript:info(0)' : saveSession('publish')"
            :class="this.disableButton ? 'disabled:cursor-wait disabled:opacity-60' : ''"
            class="
              step-publish-session
              ml-3
              inline-flex
              items-center
              px-4
              py-2
              border border-transparent
              rounded-md
              shadow-sm
              text-sm
              font-medium
              text-white
              bg-vice-rose-400
              hover:bg-vice-rose-500
              focus:outline-none
              focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
            "
            :disabled="this.disableButton"
          >
            <SaveUpdateSessionIcon ref="uploadIcon" class="h-5 w-5 mr-2" />
            Publish session
          </button>
          <button
            data-cy="update-session"
            v-else
            type="button"
            @click="this.disableButton ? 'javascript:info(0)' : saveSession(SESSION_STATUS.PUBLISH)"
            :class="this.disableButton ? 'disabled:cursor-wait disabled:opacity-60' : ''"
            class="
              ml-3
              inline-flex
              items-center
              px-4
              py-2
              border border-transparent
              rounded-md
              shadow-sm
              text-sm
              font-medium
              text-white
              bg-vice-rose-400
              hover:bg-vice-rose-500
              focus:outline-none
              focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
            "
            :disabled="this.disableButton"
          >
            <SaveUpdateSessionIcon ref="uploadIcon" class="h-5 w-5 mr-2" />
            Update session
          </button>
        </div>
      </div>
    </template>
    <TransitionRoot as="template" :show="warningOpen">
      <Dialog
        as="div"
        class="fixed zoom-80 z-20 inset-0 overflow-y-auto"
        @close="warningOpen = false"
      >
        <div
          class="
            flex
            items-end
            justify-center
            min-h-screen
            pt-4
            px-4
            pb-20
            text-center
            sm:block
            sm:p-0
          "
        >
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0"
            enter-to="opacity-100"
            leave="ease-in duration-200"
            leave-from="opacity-100"
            leave-to="opacity-0"
          >
            <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </TransitionChild>

          <!-- This element is to trick the browser into centering the modal contents. -->
          <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div
              class="
                inline-block
                align-bottom
                bg-white
                rounded-lg
                px-4
                pt-5
                pb-4
                text-left
                overflow-hidden
                shadow-xl
                transform
                transition-all
                sm:my-8
                sm:align-middle
                sm:max-w-lg
                sm:w-full
                sm:p-6
              "
            >
              <div class="p-2">
                <h3 class="text-lg leading-6 font-medium text-gray-900">Warning</h3>
                <div class="mt-2 mb-4 max-w-xl text-sm text-gray-500">
                  <p>You already have a session scheduled for this time, do you want to proceed?</p>
                </div>
                <div class="mt45">
                  <button
                    type="button"
                    class="
                      inline-flex
                      justify-center
                      rounded-md
                      border border-transparent
                      bg-blue-100
                      px-4
                      py-2
                      text-sm
                      font-medium
                      text-blue-900
                      hover:bg-blue-200
                      focus:outline-none
                      focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2
                    "
                    @click="warningOpen = false"
                  >
                    Close
                  </button>

                  <button
                    type="button"
                    class="
                      ml-4
                      inline-flex
                      justify-center
                      rounded-md
                      border border-transparent
                      bg-red-100
                      px-4
                      py-2
                      text-sm
                      font-medium
                      text-red-900
                      hover:bg-red-200
                      focus:outline-none
                      focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2
                    "
                    @click="submitSessionForm('publish')"
                  >
                    Create a session anyway
                  </button>
                </div>
              </div>
            </div>
          </TransitionChild>
        </div>
      </Dialog>
    </TransitionRoot>
    <TransitionRoot as="template" :show="open">
      <Dialog as="div" class="fixed zoom-80 z-20 inset-0 overflow-y-auto" @close="open = false">
        <div
          class="
            flex
            items-center
            justify-center
            min-h-screen
            pt-4
            px-4
            pb-20
            text-center
            sm:block
            sm:p-0
          "
        >
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0"
            enter-to="opacity-100"
            leave="ease-in duration-200"
            leave-from="opacity-100"
            leave-to="opacity-0"
          >
            <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </TransitionChild>

          <!-- This element is to trick the browser into centering the modal contents. -->
          <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div
              class="
                inline-block
                align-bottom
                bg-white
                rounded-lg
                px-4
                pt-5
                pb-4
                text-left
                overflow-hidden
                shadow-xl
                transform
                transition-all
                sm:my-8
                sm:align-middle
                sm:max-w-lg
                sm:w-full
                sm:p-6
              "
            >
              <div class="p-2">
                <h3 class="text-lg leading-6 font-medium text-gray-900">Delete your session</h3>
                <div class="mt-2 max-w-xl text-sm text-gray-500">
                  <p>Once you delete your session, you will lose all data associated with it!</p>
                </div>
                <div class="mt-5">
                  <inertia-link
                    data-cy="delete-session-confirm"
                    :href="route('delete.session', this.$page.props.session.unique_id)"
                    method="post"
                    type="button"
                    class="
                      inline-flex
                      items-center
                      justify-center
                      px-4
                      py-2
                      border border-transparent
                      font-medium
                      rounded-md
                      text-red-700
                      bg-red-100
                      hover:bg-red-200
                      focus:outline-none
                      focus:ring-2 focus:ring-offset-2 focus:ring-red-500
                      sm:text-sm
                    "
                  >
                    Delete session
                  </inertia-link>
                </div>
              </div>
            </div>
          </TransitionChild>
        </div>
      </Dialog>
    </TransitionRoot>

    <event-form :isRegister="false" class="max-w-5xl m-auto" ref="eventform"></event-form>
  </clinician-app-layout>
</template>

<script>
import { Dialog, DialogOverlay, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { ref } from 'vue';
import dayjs from 'dayjs';
import ClinicianAppLayout from '@/Layouts/ClinicianAppLayout.vue';
import EventForm from './Forms/EventForm.vue';
import { SESSION_STATUS } from '@/Utils/vice-constants';

import SessionTabs from './Forms/Mixins/SessionTabs.vue';
import useTourManager from '@/Composables/useTourManager';
import { sessionCreationSteps } from '@/Data/tourSteps';
import SaveUpdateSessionIcon from '@/Components/Icons/SaveUpdateSessionIcon.vue';
import DeleteSessionIcon from '@/Components/Icons/DeleteSessionIcon.vue';
import SaveAsDraftIcon from '@/Components/Icons/SaveAsDraftIcon.vue';

const sessionCreationTour = useTourManager('sessionCreationTour', sessionCreationSteps);

export default {
  name: 'UpdateSession',
  setup() {
    const open = ref(false);
    const warningOpen = ref(false);
    return {
      open,
      warningOpen,
      SESSION_STATUS,
    };
  },
  components: {
    Dialog,
    DialogOverlay,
    TransitionChild,
    TransitionRoot,
    ClinicianAppLayout,
    EventForm,
    SaveUpdateSessionIcon,
    DeleteSessionIcon,
    SaveAsDraftIcon,
  },
  mixins: [SessionTabs],
  props: {
    timezoneList: Array,
    errors: Object,
    event: Object,
    status: String,
  },
  mounted() {
    setTimeout(() => {
      sessionCreationTour.startTour();
    }, 500);
  },
  data() {
    return {
      disableButton: false,
    };
  },
  methods: {
    saveSession(status) {
      this.disableButton = true;
      setTimeout(() => {
        this.disableButton = false;
      }, 4000);
      if (status === 'publish') {
        this.checkIfHasScheduledSessionsOnSpecifiedTime(status);
      } else {
        this.$refs.eventform.submitForm(status);
      }
    },
    setTabState(id) {
      this.tabState(id);
      this.$refs.eventform.tabState(id);
    },
    tabClasses(tab, tabIdx) {
      const activeTabClasses = 'border-vice-teal-400 text-grey-900';
      const inactiveTabClasses =
        'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300';
      const errorTabClasses = 'text-grey-900';

      // TODO: Check if current tab has errors, not just the first one
      if (Object.keys(this.$page.props.errors).length && tabIdx === 0) return errorTabClasses;
      return tab.current ? activeTabClasses : inactiveTabClasses;
    },
    isDraft() {
      return this.$page.props.session.status === SESSION_STATUS.DRAFT;
    },
    checkIfHasScheduledSessionsOnSpecifiedTime(status) {
      const formDataClinicianId = this.$refs.eventform.$data.form.clinician_id;
      const formDataSessionStartDate = this.$refs.eventform.$data.form.session_date;
      const formDataSessionStartTime = this.$refs.eventform.$data.form.session_start;

      const startDate = dayjs(formDataSessionStartDate).format('YYYY-MM-DD');

      const requestData = {
        session_start_date_time: `${startDate} ${formDataSessionStartTime}`,
        clinician_id: formDataClinicianId,
      };

      axios
        .post(route('check-schedule.session', this.$page.props.session.unique_id), requestData)
        .then((response) => {
          const sessionsWithTheSameStartDateTimeExists =
            response.data.data.sessions_with_the_same_start_date_time_exists;
          if (sessionsWithTheSameStartDateTimeExists) {
            this.warningOpen = true;
          } else {
            this.submitSessionForm(status);
          }
        })
        .catch((e) => {
          console.error(e);
          this.submitSessionForm(status);
        });
    },
    submitSessionForm(status) {
      this.$refs.eventform.submitForm(status);
    },
  },
};
</script>
